import { create } from 'zustand'
import { applyMode, Mode } from '@cloudscape-design/global-styles'
import type {
  NavPage,
  Profile,
  PresetProfile,
  BenchmarkRun,
  BenchmarkProgress,
  StreamChunkEvent,
  NetworkProbeResult,
  TracerouteHopEvent,
  ChatMessage,
  ExecutionMode,
  CredentialStatus,
  Vendor,
  BedrockRegionKey
} from '@/types'

import { DEFAULT_PROFILES } from '@/config/defaults'
import { getSchedulerState, isSchedulerRunning } from '@/engine/scheduler-singleton'
import { getRunStats } from '@/engine/metrics'

const STORAGE_KEY = 'bedrock-diag-profiles'
const RUNS_KEY = 'bedrock-diag-runs'
const MODE_KEY = 'bedrock-diag-execution-mode'
const GATEWAY_KEY = 'bedrock-diag-api-gateway-url'
const REGION_KEY = 'bedrock-diag-region'
const VISUAL_MODE_KEY = 'bedrock-diag-visual-mode'
const CLOUD_STORAGE_KEY = 'bedrock-diag-cloud-storage'
const LOCAL_FOLDER_KEY = 'bedrock-diag-local-folder'

function loadProfilesFromStorage(): Profile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  // First visit: seed with defaults
  return DEFAULT_PROFILES
}

function saveProfilesToStorage(profiles: Profile[]): void {
  // Strip image attachments before persisting - they're too large for localStorage
  const stripped = profiles.map(p => {
    const { imageAttachments, ...rest } = p
    return rest
  })
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stripped))
}

function loadRunsFromStorage(): BenchmarkRun[] {
  try {
    const raw = localStorage.getItem(RUNS_KEY)
    if (raw) {
      const runs: BenchmarkRun[] = JSON.parse(raw)
      // Recompute stats from raw results (stats are no longer persisted)
      return runs.map(run => ({
        ...run,
        stats: (!run.stats || run.stats.length === 0) ? getRunStats(run) : run.stats,
      }))
    }
  } catch { /* ignore */ }
  return []
}

function saveRunsToStorage(runs: BenchmarkRun[]): void {
  try {
    // Strip stats (recomputed on load) and image attachments to stay within localStorage limits
    const stripped = runs.slice(0, 10).map(run => ({
      ...run,
      stats: [],
      profiles: run.profiles.map(p => {
        if ('imageAttachments' in p) {
          const { imageAttachments, ...rest } = p as any
          return { ...rest, imageCount: imageAttachments?.length || 0 }
        }
        return p
      })
    }))
    localStorage.setItem(RUNS_KEY, JSON.stringify(stripped))
  } catch (err) {
    console.warn('Failed to save runs to localStorage:', err)
  }
}

function loadExecutionMode(): ExecutionMode {
  try {
    const raw = localStorage.getItem(MODE_KEY)
    if (raw === 'cloud' || raw === 'local') return raw
  } catch { /* ignore */ }
  return 'cloud'
}

function loadApiGatewayUrl(): string {
  try {
    return localStorage.getItem(GATEWAY_KEY) ?? ''
  } catch { /* ignore */ }
  return ''
}

function loadSelectedRegion(): string {
  try {
    return localStorage.getItem(REGION_KEY) ?? 'us-east-1'
  } catch { /* ignore */ }
  return 'us-east-1'
}

function loadCloudStorageEnabled(): boolean {
  try {
    const stored = localStorage.getItem(CLOUD_STORAGE_KEY)
    if (stored === null) return true
    return stored === 'true'
  } catch { /* ignore */ }
  return true
}

function loadLocalFolderEnabled(): boolean {
  try {
    return localStorage.getItem(LOCAL_FOLDER_KEY) === 'true'
  } catch { /* ignore */ }
  return false
}

function loadVisualMode(): 'light' | 'dark' {
  try {
    const stored = localStorage.getItem(VISUAL_MODE_KEY)
    return stored === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

const VALID_PAGES: Set<string> = new Set([
  'overview', 'test', 'profiles',
  'results', 'lifecycle', 'settings', 'anthropic-health', 'openai-health', 'grok-health',
  'global-metrics', 'signin', 'capacity-blocks',
])

function loadPageFromUrl(): NavPage {
  try {
    const path = window.location.pathname.replace(/^\//, '')
    if (VALID_PAGES.has(path)) return path as NavPage
  } catch { /* SSR or test env */ }
  return 'overview'
}

interface AppState {
  currentPage: NavPage
  setPage: (page: NavPage) => void

  profiles: Profile[]
  setProfiles: (profiles: Profile[]) => void
  persistProfiles: (profiles: Profile[]) => void

  isRunning: boolean
  progress: BenchmarkProgress | null
  streamChunks: StreamChunkEvent[]
  currentRun: BenchmarkRun | null
  pastRuns: BenchmarkRun[]

  setRunning: (running: boolean) => void
  setProgress: (progress: BenchmarkProgress | null) => void
  addStreamChunk: (chunk: StreamChunkEvent) => void
  clearStreamChunks: () => void
  setCurrentRun: (run: BenchmarkRun | null) => void
  addPastRun: (run: BenchmarkRun) => void

  presets: PresetProfile[]
  presetsLoaded: boolean
  setPresets: (presets: PresetProfile[]) => void

  networkProbes: NetworkProbeResult[]
  tracerouteHops: Record<string, TracerouteHopEvent[]>
  addNetworkProbe: (probe: NetworkProbeResult) => void
  addTracerouteHop: (event: TracerouteHopEvent) => void
  clearNetworkData: () => void

  analysisText: string
  analysisLoading: boolean
  analysisError: string | null
  setAnalysisText: (text: string) => void
  setAnalysisLoading: (loading: boolean) => void
  setAnalysisError: (error: string | null) => void
  clearAnalysis: () => void

  chatMessages: ChatMessage[]
  chatLoading: boolean
  chatError: string | null
  addChatMessage: (msg: ChatMessage) => void
  setChatLoading: (loading: boolean) => void
  setChatError: (error: string | null) => void
  clearChat: () => void

  // Task 4.1: Execution mode, API Gateway URL, region (localStorage-persisted)
  executionMode: ExecutionMode
  apiGatewayUrl: string
  selectedRegion: string
  setExecutionMode: (mode: ExecutionMode) => void
  setApiGatewayUrl: (url: string) => void
  setSelectedRegion: (region: string) => void

  // Task 4.2: Credential status (in-memory only)
  credentialStatus: CredentialStatus
  credentialError: string | null
  ssoExpiration: string | null
  setCredentialStatus: (status: CredentialStatus, error?: string) => void
  setSsoExpiration: (expiration: string | null) => void

  // AWS account ID from STS GetCallerIdentity (in-memory only)
  awsAccountId: string | null
  setAwsAccountId: (accountId: string | null) => void

  // Task 4.4: API key presence flags (in-memory only)
  hasAnthropicKey: boolean
  hasXaiKey: boolean
  setApiKeyState: (anthropic: boolean, xai: boolean) => void

  // Vendor filter and Bedrock region (in-memory only)
  selectedVendors: Set<Vendor>
  bedrockRegion: BedrockRegionKey
  toggleVendor: (vendor: Vendor) => void
  setBedrockRegion: (region: BedrockRegionKey) => void

  // Visual mode (light/dark)
  visualMode: 'light' | 'dark'
  setVisualMode: (mode: 'light' | 'dark') => void

  // Cognito auth state (in-memory only)
  isAuthenticated: boolean
  userAlias: string | null
  jwtToken: string | null
  cognitoLoading: boolean
  setAuthState: (authenticated: boolean, alias: string | null, token: string | null) => void
  setCognitoLoading: (loading: boolean) => void
  clearAuthState: () => void

  // Cloud storage toggle for local mode (localStorage-persisted)
  cloudStorageEnabled: boolean
  setCloudStorageEnabled: (enabled: boolean) => void

  // Local folder storage via File System Access API (localStorage-persisted)
  localFolderEnabled: boolean
  setLocalFolderEnabled: (enabled: boolean) => void

  // Scheduler state (read from scheduler singleton)
  schedulerRunning: boolean
  schedulerTotalRuns: number
  schedulerLastRunTime: string | null
  schedulerCurrentlyRunning: boolean
  schedulerProgress: BenchmarkProgress | null
  refreshSchedulerState: () => void
}

export const useAppStore = create<AppState>((set) => ({
  currentPage: loadPageFromUrl(),
  setPage: (page) => {
    // Push to browser history so URL stays in sync
    const target = `/${page}`
    if (window.location.pathname !== target) {
      window.history.pushState({ page }, '', target)
    }
    set({ currentPage: page })
  },

  profiles: loadProfilesFromStorage(),
  setProfiles: (profiles) => set({ profiles }),
  persistProfiles: (profiles) => {
    saveProfilesToStorage(profiles)
    set({ profiles })
  },

  isRunning: false,
  progress: null,
  streamChunks: [],
  currentRun: null,
  pastRuns: loadRunsFromStorage(),

  setRunning: (running) => set({ isRunning: running }),
  setProgress: (progress) => set({ progress }),
  addStreamChunk: (chunk) =>
    set((state) => ({
      streamChunks: [...state.streamChunks.slice(-500), chunk]
    })),
  clearStreamChunks: () => set({ streamChunks: [] }),
  setCurrentRun: (run) => set({ currentRun: run }),
  addPastRun: (run) =>
    set((state) => {
      const updated = [run, ...state.pastRuns].slice(0, 20)
      saveRunsToStorage(updated)
      return { pastRuns: updated }
    }),

  presets: [],
  presetsLoaded: false,
  setPresets: (presets) => {
    // Persist preset enabled/disabled and custom prompt/maxTokens state
    try {
      const state: Record<string, { enabled: boolean; prompt?: string; maxTokens?: number }> = {}
      for (const p of presets) {
        state[p.id] = { enabled: p.enabled, prompt: p.prompt, maxTokens: p.maxTokens }
      }
      localStorage.setItem('bedrock-diag-preset-state', JSON.stringify(state))
    } catch { /* noop */ }
    set({ presets, presetsLoaded: true })
    // Sync to cloud if authenticated (debounced)
    import('@/engine/settings-sync').then(m => m.scheduleSyncToCloud()).catch(() => {})
  },

  networkProbes: [],
  tracerouteHops: {},
  addNetworkProbe: (probe) =>
    set((state) => ({ networkProbes: [...state.networkProbes, probe] })),
  addTracerouteHop: (event) =>
    set((state) => ({
      tracerouteHops: {
        ...state.tracerouteHops,
        [event.host]: [...(state.tracerouteHops[event.host] ?? []), event]
      }
    })),
  clearNetworkData: () => set({ networkProbes: [], tracerouteHops: {} }),

  analysisText: '',
  analysisLoading: false,
  analysisError: null,
  setAnalysisText: (text) => set({ analysisText: text }),
  setAnalysisLoading: (loading) => set({ analysisLoading: loading }),
  setAnalysisError: (error) => set({ analysisError: error }),
  clearAnalysis: () => set({ analysisText: '', analysisLoading: false, analysisError: null }),

  chatMessages: [],
  chatLoading: false,
  chatError: null,
  addChatMessage: (msg) => set((state) => ({ chatMessages: [...state.chatMessages, msg] })),
  setChatLoading: (loading) => set({ chatLoading: loading }),
  setChatError: (error) => set({ chatError: error }),
  clearChat: () => set({ chatMessages: [], chatLoading: false, chatError: null }),

  // Task 4.1: Execution mode, API Gateway URL, region
  executionMode: loadExecutionMode(),
  apiGatewayUrl: loadApiGatewayUrl(),
  selectedRegion: loadSelectedRegion(),

  setExecutionMode: (mode) =>
    set((state) => {
      console.info(`[App] Execution mode changed from ${state.executionMode} to ${mode}`)
      localStorage.setItem(MODE_KEY, mode)
      return { executionMode: mode }
    }),

  setApiGatewayUrl: (url) => {
    if (url !== '' && !url.startsWith('https://')) {
      console.warn('[App] API Gateway URL rejected: must start with https://')
      return
    }
    localStorage.setItem(GATEWAY_KEY, url)
    set({ apiGatewayUrl: url })
  },

  setSelectedRegion: (region) => {
    localStorage.setItem(REGION_KEY, region)
    set({ selectedRegion: region })
  },

  // Task 4.2: Credential status (in-memory only)
  credentialStatus: 'none',
  credentialError: null,
  ssoExpiration: null,

  setCredentialStatus: (status, error) =>
    set({ credentialStatus: status, credentialError: error ?? null }),

  setSsoExpiration: (expiration) => set({ ssoExpiration: expiration }),

  // AWS account ID from STS GetCallerIdentity (in-memory only)
  awsAccountId: null,
  setAwsAccountId: (accountId) => set({ awsAccountId: accountId }),

  // Task 4.4: API key presence flags (in-memory only)
  hasAnthropicKey: false,
  hasXaiKey: false,
  setApiKeyState: (anthropic, xai) =>
    set({ hasAnthropicKey: anthropic, hasXaiKey: xai }),

  // Vendor filter and Bedrock region (in-memory only)
  selectedVendors: new Set<Vendor>(['bedrock', 'anthropic-direct', 'grok']),
  bedrockRegion: 'US' as BedrockRegionKey,

  toggleVendor: (vendor) =>
    set((state) => {
      const next = new Set(state.selectedVendors)
      if (next.has(vendor)) {
        next.delete(vendor)
      } else {
        next.add(vendor)
      }
      return { selectedVendors: next }
    }),

  setBedrockRegion: (region) => set({ bedrockRegion: region }),

  // Visual mode (light/dark)
  visualMode: loadVisualMode(),
  setVisualMode: (mode) => {
    localStorage.setItem(VISUAL_MODE_KEY, mode)
    applyMode(mode === 'light' ? Mode.Light : Mode.Dark)
    set({ visualMode: mode })
    import('@/engine/settings-sync').then(m => m.scheduleSyncToCloud()).catch(() => {})
  },

  // Cognito auth state (in-memory only)
  isAuthenticated: false,
  userAlias: null,
  jwtToken: null,
  cognitoLoading: false,
  setAuthState: (authenticated, alias, token) =>
    set({ isAuthenticated: authenticated, userAlias: alias, jwtToken: token }),
  setCognitoLoading: (loading) => set({ cognitoLoading: loading }),
  clearAuthState: () =>
    set({ isAuthenticated: false, userAlias: null, jwtToken: null }),

  // Cloud storage toggle for local mode
  cloudStorageEnabled: loadCloudStorageEnabled(),
  setCloudStorageEnabled: (enabled) => {
    localStorage.setItem(CLOUD_STORAGE_KEY, String(enabled))
    set({ cloudStorageEnabled: enabled })
  },

  // Local folder storage via File System Access API
  localFolderEnabled: loadLocalFolderEnabled(),
  setLocalFolderEnabled: (enabled) => {
    localStorage.setItem(LOCAL_FOLDER_KEY, String(enabled))
    set({ localFolderEnabled: enabled })
  },

  // Scheduler state (read from scheduler singleton)
  schedulerRunning: false,
  schedulerTotalRuns: 0,
  schedulerLastRunTime: null,
  schedulerCurrentlyRunning: false,
  schedulerProgress: null,
  refreshSchedulerState: () => {
    const state = getSchedulerState()
    set({
      schedulerRunning: isSchedulerRunning(),
      schedulerTotalRuns: state.totalRuns,
      schedulerLastRunTime: state.lastRunTime,
      schedulerCurrentlyRunning: state.currentlyRunning,
      schedulerProgress: state.progress,
    })
  },
}))
